/*
 * @fileOverView Modal component definition
 */

import Vue from 'vue';
import Throttle from 'utils/Throttle';
import {KeyCodes, Events, Intervals} from 'utils/Constants';
import ViewportHelper from 'utils/ViewportHelper';
import KeyboardHelper from 'utils/KeyboardHelper'
import focusable from 'jquery-ui/ui/focusable';
import LazyLoad from 'mixins/LazyLoad.mixin';

/**
 * A reference to the refs used in this view
 *
 * @property REFS
 * @type {Object}
 * @private
 */
const REFS = {
  'MODAL_INNER': 'js-modal-inner'
};

/**
 * A reference to the selectors used in this view
 *
 * @property SELECTORS
 * @type {Object}
 * @private
 */
const SELECTORS = {
  'TRIGGER': '.js-modal-trigger',
  'BODY': 'body',
  'BEFORE_CONTENT': '.js-beforeContent',
  'AFTER_CONTENT': '.js-afterContent',
  'OUTSIDE_IGNORE': '.js-modal-outside-ignore'
};

/**
 * A reference to the classes used in this view
 *
 * @property CLASSES
 * @type {Object}
 * @private
 */
const CLASSES = {
  'NO_SCROLL': 'noScroll',
  'MODAL_READABLE': 'js-modalContent-readable',
};

/**
 * [Modal Vue]
 * @type {class}
 */
export default {
  name: 'modal',

  mixins: [
    LazyLoad
  ],

  props: {
    'modalName': String,
    'closeOnBack': {
      default: false,
      type: Boolean
    },
    'allowBodyScroll': {
      default: false,
      type: Boolean
    },
    'positionRelativeTrigger': {
      default: false,
      type: Boolean
    },
    'allowFocusPrevious': {
      default: true,
      type: Boolean
    },
    'startingSlide': {
      default: -1,
      type: Number
    },
    'waitToOpenOn': Boolean
  },

  data: function() {
    return {
      isExpanded: false,
      previousBodyScrollTop: 0
    }
  },

  computed: {
    /**
     * [firstFocusableItem this is the first focusable item that is not the trap item]
     * @type {Object}
     */
    firstFocusableItem: {
      get: function() {
        if (!this._$focusableContent) {
          this._$focusableContent = $(this.$refs.modal).find('iframe, :focusable').not(SELECTORS.BEFORE_CONTENT).not(SELECTORS.AFTER_CONTENT);
        }
        return this._$focusableContent[0];
      }
    },

    /**
     * [lastFocusableItem this is the last focusable item that is not the trap item]
     * @type {Object}
     */
    lastFocusableItem: {
      get: function() {
        if (!this._$focusableContent) {
          this._$focusableContent = $(this.$refs.modal).find('iframe, :focusable').not(SELECTORS.BEFORE_CONTENT).not(SELECTORS.AFTER_CONTENT);
        }
        return this._$focusableContent[this._$focusableContent.length - 1];
      }
    }
  },

  watch: {
    isExpanded: function(val) {
      if (val) {
        this.onAfterShow();
      }
      else {
        this.onAfterClose();
      }
    },

    startingSlide: function(val) {
      if (val >= 0) {
        this.isExpanded = true;
      }
    },

    waitToOpenOn: function(val) {
      if (val && !this.isExpanded) {
        this.isExpanded = true;
      }
    }
  },

  created() {
    this.handleBodyClick = this.handleClickOutside.bind(this);
    this.handleWindowResize = Throttle(() => this.onWindowResize(), Intervals.RESIZE_INTERVAL);
    this.handleBackButton = this.onHistoryPopState.bind(this);
  },

  mounted() {
    if (this.modalName) {
      $('body').on('click', `${SELECTORS.TRIGGER}[data-modal-name="${this.modalName}"]`, this.handleTriggerClick);
    }

    this.$body = $(SELECTORS.BODY);
  },

  methods: {

    handleTriggerClick() {
      this.isExpanded = !this.isExpanded;
    },

    /**
     * Handles the event when clicking outside of the modal
     * @param  {event} e [click event]
     * @return {void}
     */
    handleClickOutside: function(e) {
      if (this.isExpanded && this.isOutside(e.target) && !this.isTrigger(e.target)) {
        // The js-modal-outside-ignore class allows non-modal elements to be
        // clicked without triggering the modal close. This is helpful for
        // combinations like the cookie dialog and newsletter modal.
        if (e.target.closest(SELECTORS.OUTSIDE_IGNORE) && this.$refs.close) {
          // Return focus within the modal.
          this.$refs.close.focus();
        }
        else {
          this.isExpanded = false;
        }
      }
    },

    /**
     * [isOutside determines if the click is outside of the content. Used for centered modal content]
     * @param  {element}  target [the event target]
     * @return {Boolean}        [true if outside, false if not or inner is not defined]
     */
    isOutside(target) {
      const inner = this.$refs[REFS.MODAL_INNER];
      if (inner) {
        return !inner.contains(target) && inner !== target;
      }
      return false;
    },

    /**
     * [isTrigger determines if the target of the click was also the trigger]
     * @param  {element}  target [the event target]
     * @return {Boolean}        [true if it was the trigger, false if not]
     */
    isTrigger(target) {
      let isTrigger = false;
      if (!this.$trigger || this.$trigger.length < 1) return isTrigger;

      this.$trigger.each((idx, $trigger) => {
        if ($trigger.contains(target) || $trigger === target)
          isTrigger = true;
      });

      return isTrigger;
    },

    /**
     * [onBeforeShow hook for handling things after the modal is shown]
     * @return {void}
     */
    onAfterShow() {
      this.lazyLoad({
        'container': '.js-modal-content'
      });

      this.previousFocus = document.activeElement;
      window.addEventListener(Events.RESIZE, this.handleWindowResize);
      document.body.addEventListener(Events.CLICK, this.handleBodyClick);

      if (!this.allowBodyScroll || ViewportHelper.isMobile()) {
        // Track where the page was scrolled to before disabling scrolling so it
        // doesn't jump to the top. A bit weird, but required when using `fixed`
        // positioning to disable scrolling, which is needed for iOS.
        this.previousBodyScrollTop = (document.documentElement.scrollTop || document.body.scrollTop);
        document.body.style.top = (-1 * this.previousBodyScrollTop) + 'px';
        this.$body.addClass(CLASSES.NO_SCROLL);

        // Scroll back to the top of a previously scrolled modal.
        $(this.$refs[REFS.MODAL_INNER]).animate({scrollTop: 0}, 10);
      }

      // Add padding for Drupal Toolbar
      if (document.body.style.paddingTop) {
        this.$refs.modal.style.paddingTop = document.body.style.paddingTop;
      }

      this.$nextTick(() => {
        if (this.$refs.modal && this.$refs.modal.classList.contains(CLASSES.MODAL_READABLE)) {
          // Ensure modal container is focusable
          this.$refs.modal.setAttribute('tabindex', '-1');

          // Focus the modal for screen readers
          this.$refs.modal.focus();
        }

        // Optionally focus the close button after modal is shown.
        this.$refs.close && this.$refs.close.focus();
      });

      if (this.closeOnBack) {
        history.pushState(null, null, window.location.href);
        window.addEventListener(Events.POPSTATE, this.onHistoryPopState);
      }
    },

    /**
     * [onAfterClose hook for handling things after the modal closes]
     * @return {void}
     */
    onAfterClose() {
      window.removeEventListener(Events.RESIZE, this.handleWindowResize);
      document.body.removeEventListener(Events.CLICK, this.handleBodyClick);

      // Reenable page scrolling and return it to where it was before disabling.
      this.$body.removeClass(CLASSES.NO_SCROLL);

      if (!this.allowBodyScroll) {
        document.body.style.top = '';
        document.documentElement.scrollTop = document.body.scrollTop = this.previousBodyScrollTop;
      }

      if (this.previousFocus && this.allowFocusPrevious) {
        this.previousFocus.focus();
      }

      this.previousFocus = null;
      if (this.resetActiveStates)
        this.resetActiveStates();

      this.$emit('modal-close');
    },

    /**
     * [handleFocusBeforeContent will handle focus trapping in the modal]
     * @return {void}
     */
    handleFocusBeforeContent(e) {
      if (this.direction === 'forward'){
        this.focusItem(this.firstFocusableItem);
      }
      else {
        this.focusItem(this.lastFocusableItem);
      }
    },

    /**
     * [handleFocusAfterContent will handle focus trapping in the modal]
     * @return {void}
     */
    handleFocusAfterContent(e) {
      if (this.direction === 'back') {
        this.focusItem(this.lastFocusableItem);
      }
      else {
        this.focusItem(this.firstFocusableItem);
      }
    },

    /**
     * [handleTab handles tab events on modal. Here we just set the direction]
     * @param  {event} e [tab event]
     * @return {void}
     */
    handleTab(e) {
      e.stopPropagation();

      if (KeyboardHelper.isTab(e)) {
        this.direction = 'forward';
      }
      else if(KeyboardHelper.isTabBack(e)) {
        this.direction = 'back';
      }
    },

    /**
     * [focusItem handles focusing the first or last item as users tab around]
     * @param  {jquery element} $item [the jquery wrapped item to focus]
     * @return {void}
     */
    focusItem($item) {
      if ($item) {
        $item.focus();
      }
    },

    /**
     * [onWindowResize handle window resize, focusable items can change at different viewports]
     * @return {void}
     */
    onWindowResize() {
      this._$focusableContent = null;

      // Adjust padding for Drupal Toolbar
      if (document.body.style.paddingTop) {
        this.$refs.modal.style.paddingTop = document.body.style.paddingTop;
      }
    },

    /**
     * [onHistoryPopState close modal on backbutton click]
     * @return {void}
     */
    onHistoryPopState() {
      //this needs to happen immediately
      window.removeEventListener(Events.POPSTATE, this.onHistoryPopState);
      this.isExpanded = false;
    }
  }
};
